import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, makeStyles, Tab, tokens } from "@fluentui/react-components";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { fetchRelationContact } from "./relation-contact-slice";
import { fetchVacancyUids } from "../vacancy/vacancies-uids-slice";
import { ENTITY_RELATION_CONTACT } from "../../utils/Constants";
import TabHeader from "../../blocks/tab/TabHeader";
import TabContent from "../../blocks/tab/TabContent";
import RelationContactInfo from "./RelationContactInfo";
import Notes from "../note/Notes";
import DocumentSelection from "../document/DocumentSelection";
import Vacancies from "../vacancy/Vacancies";
import PageHeader from "../../blocks/PageHeader";
import BottomFixedButton from "../../blocks/BottomFixedButton";

const RelationContactPage = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [document, setDocument] = useState(null);
  const [selectedTab, setSelectedTab] = useState(location.state?.selectedTab || "general");
  const skipPageHeader = location.state?.skipPageHeader;
  const isEntitySelection = location.state?.isEntitySelection;

  const dispatch: AppDispatch = useDispatch();
  const { relationContact, loading } = useSelector((state: RootState) => state.relationContact);
  const { vacancyUids } = useSelector((state: RootState) => state.vacancyUids);

  useEffect(() => {
    if (uid) {
      dispatch(fetchRelationContact(uid));
      dispatch(fetchVacancyUids({ uid, entity: ENTITY_RELATION_CONTACT }));
    }
  }, [dispatch, uid]);

  const handleDocReady = (document) => {
    if (document) {
      setDocument(document);
      setSelectedTab("upload");
    }
  };

  const handleDocDetails = () => {
    navigate("/document-details", {
      state: { uid, entity: ENTITY_RELATION_CONTACT, document, skipPageHeader, selectedTab },
    });
  };

  const handleNoteForm = () => {
    navigate("/note-form", {
      state: { uid, entity: ENTITY_RELATION_CONTACT, skipPageHeader, selectedTab },
    });
  };

  return (
    <>
      {!skipPageHeader && (
        isEntitySelection ? (
          <PageHeader 
            onBack={() => navigate(-1)} 
            onNext={() => navigate(`/relation/${relationContact.relationId}`)} 
            nextLabel="Relation" />
        ) : (
          <PageHeader onNext={() => navigate(`/relation/${relationContact.relationId}`)} nextLabel="Relation" />
        )
      )}

      {!loading && relationContact && (
        <>
          <TabHeader
            name={`${relationContact.Person?.firstName || ""} ${relationContact.Person?.infix || ""} ${relationContact.Person?.lastName || ""}`}
            status={relationContact.Person?.statusName}
            profileImage={relationContact?.profileImage}
            entity={ENTITY_RELATION_CONTACT}
            entityId={relationContact?.uid}
          />
          <TabContent selectedTab={selectedTab} skipPageHeader={skipPageHeader} onTabChange={setSelectedTab}>
            <Tab value="general" name="General">
              <RelationContactInfo relationContact={relationContact} />
            </Tab>
            <Tab value="vacancies" name="Vacancies">
              <Vacancies uids={vacancyUids} />
            </Tab>
            <Tab value="notes" name="Notes">
              <>
                <Notes entity={ENTITY_RELATION_CONTACT} uid={relationContact?.uid} />
                <BottomFixedButton appearance="primary" onClick={handleNoteForm} buttonText="New note" />
              </>
            </Tab>
            <Tab value="upload" name="Upload Documents">
              <>
                <DocumentSelection onReady={handleDocReady} />
                <BottomFixedButton
                  appearance="primary"
                  onClick={handleDocDetails}
                  disabled={!document}
                  buttonText="Next" />
              </>
            </Tab>
          </TabContent>
        </>
      )}
    </>
  );
};

export default RelationContactPage;
